import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import useLazyQuery from '../../../../../hooks/useLazyQuery';

import {
  UPDATE_COMMUNITY_SETTINGS,
  GET_COMMUNITIES
} from '../../../../../../graphql/Survey';

import CustomCopyField from '../WebSurveyCustomCopy/components/CustomCopyField';
import Dropdown from './components/Dropdown/Dropdown';

import DutchStrings from '../../../../../../../users/strings/nl';
import FrenchStrings from '../../../../../../../users/strings/fr';
import EnglishStrings from '../../../../../../../users/strings/en';
import GermanStrings from '../../../../../../../users/strings/de';

import { getCustomCopyFieldValue } from '../WebSurveyCustomCopy/WebSurveyCustomCopy';
import copyLinkToClipboard from '../../helpers/copyLinkToClipboard';

import arrowDownPurple from '../../../../../../assets/img/arrow-down-purple.svg';

import {
  defaultCommunityTag,
  allSurveysVisibleOptions,
  communityTypeOptions,
  seeGiveawayMessageOptions
} from './defaultCommunitySettings';

import styles from './Conversion.module.css';

const Conversion = ({ survey, updateCustomCopies }) => {
  const [expanded, setExpanded] = useState(false);
  const [linkCopied, setLinkCopied] = useState({});
  const [communitySettings, setCommunitySettings] = useState(
    survey && survey.communitySettings
      ? {
          ...survey.communitySettings,
          profilingTags:
            (survey.communitySettings.profilingTags &&
              survey.communitySettings.profilingTags.length &&
              survey.communitySettings.profilingTags.map(
                pT => pT.profilingTag
              )) ||
            []
        }
      : {}
  );
  const [resize, setResize] = useState(false);

  const [updateCommunitySettings] = useMutation(UPDATE_COMMUNITY_SETTINGS);

  const loadCommunitiesQuery = useLazyQuery(GET_COMMUNITIES, {
    fetchPolicy: 'no-cache'
  });

  const searchCommunities = async (page, search) => {
    const variables = {
      start: 0,
      end: (page + 1) * 10,
      search,
      salt: Math.random().toString()
    };

    const searchResults = await loadCommunitiesQuery({
      variables
    });

    return searchResults &&
      searchResults.data &&
      searchResults.data.getCommunities &&
      searchResults.data.getCommunities.communityTags &&
      searchResults.data.getCommunities.communityTags.length
      ? searchResults.data.getCommunities.communityTags.sort().map(tag => ({
          label: tag,
          value: tag
        }))
      : [];
  };

  const surveyLanguage = (survey && survey.language) || 'English';

  const customCopies =
    survey && survey.customCopies && survey.customCopies.length
      ? survey.customCopies
      : [];

  const strings = {
    Dutch: DutchStrings,
    French: FrenchStrings,
    English: EnglishStrings,
    German: GermanStrings
  };

  const translations = strings[surveyLanguage];

  const translation =
    communitySettings && communitySettings.seeGiveawayMessage
      ? 'WOULD_YOU_LIKE_TO_HAVE_MORE_IMPACT_ON_DECISIONS_BY_PARTICIPATING_IN_SURVEYS'
      : 'WOULD_YOU_LIKE_TO_HAVE_MORE_IMPACT';

  const translationFields = {
    [translation]: getCustomCopyFieldValue(
      translation,
      surveyLanguage,
      customCopies,
      translations
    )
  };

  const [customCopyFields, setCustomCopyFields] = useState({});

  const mailchimpLink = `${window.location.origin.toString()}/users/campaign/${
    survey.id
  }/user/${survey.client.id}/referral/*|EMAIL|*?userEmailSuggestion=*|EMAIL|*`;

  useEffect(
    () => {
      if (survey) {
        setCustomCopyFields(translationFields);
      }
    },
    [survey, communitySettings.seeGiveawayMessage]
  );

  const getPropertyLabel = (property, options) =>
    options.some(option => option.value === property)
      ? options.find(option => option.value === property).label
      : null;

  const getSelectedCommunities = () => {
    let selectedValue = 'General BUFFL community';
    if (
      communitySettings.profilingTags &&
      communitySettings.profilingTags.length
    ) {
      if (
        communitySettings.profilingTags.length === 1 &&
        communitySettings.profilingTags[0] &&
        communitySettings.profilingTags[0].tagValue
      ) {
        selectedValue = communitySettings.profilingTags[0].tagValue;
      } else {
        selectedValue = `${
          communitySettings.profilingTags.length
        } communities selected`;
      }
    }
    return selectedValue;
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.titleContainer}
        role="presentation"
        onClick={() => setExpanded(() => setExpanded(!expanded))}
      >
        <div className={styles.title}>Conversion (admin only)</div>
        <img
          src={arrowDownPurple}
          alt="ExpandColapse"
          className={expanded ? styles.arrowExpanded : styles.arrowCollapsed}
        />
      </div>
      {expanded ? (
        <div className={styles.content}>
          <div className={`${styles.row} ${styles.flexRow}`}>
            <div className={styles.flexColumn}>
              <div className={styles.rowText} role="presentation">
                Add respondents to community
              </div>
              <Dropdown
                selectedValue={getSelectedCommunities()}
                onSelectCompleted={async value => {
                  const newProfilingTags = [
                    ...communitySettings.profilingTags
                  ].map(profilingTag => {
                    const pT = { ...profilingTag };
                    delete pT.__typename;
                    return pT;
                  });
                  // Remove from state if exists else add it
                  if (
                    communitySettings.profilingTags.some(
                      profilingTag => profilingTag.tagValue === value
                    )
                  ) {
                    newProfilingTags.splice(
                      communitySettings.profilingTags.findIndex(
                        profilingTag => profilingTag.tagValue === value
                      ),
                      1
                    );
                  } else {
                    newProfilingTags.push({
                      ...defaultCommunityTag,
                      tagValue: value
                    });
                  }

                  let variables = {
                    survey: survey.id,
                    profilingTags: newProfilingTags
                  };

                  if (
                    newProfilingTags.length === 0 &&
                    communitySettings.allSurveysVisible === false
                  ) {
                    variables = {
                      ...variables,
                      allSurveysVisible: true
                    };
                  }

                  setCommunitySettings({
                    ...communitySettings,
                    profilingTags: newProfilingTags,
                    allSurveysVisible:
                      newProfilingTags.length === 0
                        ? true
                        : communitySettings.allSurveysVisible
                  });

                  await updateCommunitySettings({
                    variables
                  });
                }}
                searchEnabled
                fetchOptions={searchCommunities}
                loading={false}
                findList={
                  communitySettings.profilingTags &&
                  communitySettings.profilingTags.length
                    ? communitySettings.profilingTags.map(
                        profilingTag => profilingTag.tagValue
                      )
                    : []
                }
                addNew
              />
            </div>
            <div className={styles.flexColumn}>
              <div className={styles.rowText}>
                Can respondents see general BUFFL surveys (all topics)?
              </div>
              <Dropdown
                selectedValue={getPropertyLabel(
                  communitySettings.allSurveysVisible,
                  allSurveysVisibleOptions
                )}
                onSelectCompleted={async value => {
                  setCommunitySettings({
                    ...communitySettings,
                    allSurveysVisible: value
                  });

                  updateCommunitySettings({
                    variables: {
                      survey: survey.id,
                      allSurveysVisible: value
                    }
                  });
                }}
                searchEnabled={false}
                options={allSurveysVisibleOptions}
                disabled={!communitySettings.profilingTags.length}
              />
            </div>
          </div>
          <div className={`${styles.row} ${styles.flexRow}`}>
            <div className={styles.flexColumn}>
              <div className={styles.rowText}>
                Would you like to convert respondents to web or mobile app?
              </div>
              <Dropdown
                selectedValue={getPropertyLabel(
                  communitySettings.communityType,
                  communityTypeOptions
                )}
                onSelectCompleted={value => {
                  setCommunitySettings({
                    ...communitySettings,
                    communityType: value
                  });

                  updateCommunitySettings({
                    variables: {
                      survey: survey.id,
                      communityType: value
                    }
                  });
                }}
                searchEnabled={false}
                options={communityTypeOptions}
              />
            </div>
            <div className={styles.flexColumn}>
              <div className={styles.rowText}>
                Would you like to add a giveaway to convert more respondents?
              </div>
              <Dropdown
                selectedValue={getPropertyLabel(
                  communitySettings.seeGiveawayMessage,
                  seeGiveawayMessageOptions
                )}
                onSelectCompleted={async value => {
                  await setCommunitySettings({
                    ...communitySettings,
                    seeGiveawayMessage: value
                  });
                  setResize(!resize);

                  updateCommunitySettings({
                    variables: {
                      survey: survey.id,
                      seeGiveawayMessage: value
                    }
                  });
                }}
                searchEnabled={false}
                options={seeGiveawayMessageOptions}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.rowText}>
              Ask respondents to leave their email
            </div>
            <CustomCopyField
              propertyName="askForEmailAddressInWebSurvey"
              field={{
                name: translation,
                value: customCopyFields[translation]
              }}
              updateCustomCopies={updateCustomCopies}
              customCopyFields={customCopyFields}
              setCustomCopyFields={setCustomCopyFields}
              language={surveyLanguage}
              resize={resize}
            />
          </div>
          <div className={styles.row}>
            <div className={styles.rowText}>
              Copy &amp; paste this link in Mailchimp
            </div>
            <div className={styles.linkContainer}>
              <div className={styles.link}>
                <div className={styles.url}>{mailchimpLink}</div>
              </div>
              <div
                className={
                  linkCopied.mailchimp === true
                    ? `${styles.copyLinkButton} ${styles.copied}`
                    : styles.copyLinkButton
                }
                onClick={() =>
                  copyLinkToClipboard(
                    mailchimpLink,
                    'mailchimp',
                    linkCopied,
                    setLinkCopied
                  )
                }
                role="presentation"
              >
                {linkCopied.mailchimp === true ? 'Copied' : 'Copy'}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Conversion;
